/**
 * Internal module: lazily-stored init config + fetch helper.
 *
 * `initStoreApi` is called once at app boot; subsequent imperative calls
 * and the `useLicense` hook read from the stored config. The package
 * never imports an identity layer — the bearer-token getter is a plain
 * callback provided by the consumer.
 */

import { StoreApiError } from './types';

export interface StoreApiConfig {
  /** alchemie-commerce Functions origin, no trailing slash. */
  apiBase: string;
  /**
   * Optional origin for GET `/userProfile` (Firebase HTTPS function or commerce).
   * When unset, the same origin as {@link StoreApiConfig.apiBase} is used.
   * Pass an empty string (or whitespace-only) for same-origin requests (e.g. Vite dev proxy).
   */
  userProfileReadBase?: string;
  /**
   * Callback returning a fresh Firebase ID token (or any other bearer-
   * format token) for the current signed-in user. Return `null` when no
   * user is available.
   */
  getBearerToken: () => Promise<string | null>;
}

let config: StoreApiConfig | null = null;

/**
 * Redirect the browser to `url`. Extracted so tests can replace it without
 * fighting jsdom's non-configurable `window.location.assign` — in unit tests
 * call `vi.spyOn(clientModule, 'redirectTo').mockImplementation(() => {})`.
 */
export function redirectTo(url: string): void {
  if (typeof window === 'undefined') {
    throw new StoreApiError('unknown', 'Cannot redirect outside a browser environment.');
  }
  window.location.assign(url);
}

function normalizeOptionalOrigin(raw: string | undefined): string | undefined {
  if (raw === undefined) return undefined;
  return raw.trim().replace(/\/$/, '');
}

export function initStoreApi(incoming: StoreApiConfig): void {
  const apiBase = incoming.apiBase?.trim().replace(/\/$/, '') ?? '';
  if (!apiBase) {
    throw new StoreApiError(
      'not-configured',
      'initStoreApi requires a non-empty apiBase (the alchemie-commerce Functions origin).'
    );
  }
  if (typeof incoming.getBearerToken !== 'function') {
    throw new StoreApiError(
      'not-configured',
      'initStoreApi requires a getBearerToken callback returning Promise<string | null>.'
    );
  }
  const usb = normalizeOptionalOrigin(incoming.userProfileReadBase);
  config = {
    apiBase,
    getBearerToken: incoming.getBearerToken,
    userProfileReadBase: usb === undefined ? undefined : usb,
  };
}

/**
 * @internal — tests reset global state between cases.
 */
export function __resetStoreApi(): void {
  config = null;
}

export function getConfig(): StoreApiConfig {
  if (!config) {
    throw new StoreApiError(
      'not-initialized',
      'Store API not initialized. Call initStoreApi({ apiBase, getBearerToken }) at app startup.'
    );
  }
  return config;
}

// ---------------------------------------------------------------------------
// Internal fetch helper
// ---------------------------------------------------------------------------

export interface FetchOptions {
  method: 'GET' | 'POST' | 'PUT';
  relativePath: string;
  body?: unknown;
  /**
   * Request origin without trailing slash. Defaults to the configured commerce `apiBase`.
   */
  baseUrl?: string;
  /**
   * When true (default), requires a bearer token; throws `not-signed-in`
   * if the token getter returns null.
   */
  requireAuth?: boolean;
  /** Human-readable label used in error messages. */
  errorContext: string;
  /** When fetch fails before a response (network), overrides the default `"{errorContext}: network error."` message. */
  networkErrorMessage?: string;
}

export interface FetchJsonResult<T> {
  ok: true;
  data: T;
}

export async function fetchJson<T>(opts: FetchOptions): Promise<T> {
  const { apiBase, getBearerToken } = getConfig();
  const resolvedBase =
    opts.baseUrl !== undefined
      ? normalizeOptionalOrigin(opts.baseUrl) ?? ''
      : undefined;
  const requestBase = resolvedBase !== undefined ? resolvedBase : apiBase;
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  if (opts.requireAuth !== false) {
    const token = await getBearerToken();
    if (!token) {
      throw new StoreApiError(
        'not-signed-in',
        `${opts.errorContext} requires a signed-in user.`
      );
    }
    headers.Authorization = `Bearer ${token}`;
  }

  const path = opts.relativePath.startsWith('/') ? opts.relativePath : `/${opts.relativePath}`;

  let res: Response;
  try {
    res = await fetch(`${requestBase}${path}`, {
      method: opts.method,
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    });
  } catch (err) {
    throw new StoreApiError(
      'network',
      opts.networkErrorMessage ?? `${opts.errorContext}: network error.`,
      undefined
    );
  }

  const raw = await res.text();
  let data: unknown = null;
  if (raw.length > 0) {
    try {
      data = JSON.parse(raw);
    } catch {
      if (!res.ok) {
        throw new StoreApiError(
          'server',
          `${opts.errorContext} failed (${res.status}): non-JSON response.`,
          res.status
        );
      }
      throw new StoreApiError(
        'invalid-response',
        `${opts.errorContext}: server returned non-JSON body.`
      );
    }
  }

  if (!res.ok) {
    const message =
      typeof data === 'object' && data !== null && 'error' in data &&
      typeof (data as { error: unknown }).error === 'string'
        ? (data as { error: string }).error
        : `${opts.errorContext} failed (${res.status}).`;
    throw new StoreApiError('server', message, res.status);
  }

  return data as T;
}
